import { Brain, RotateCcw, SkipForward } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useScenarioPlayer, type Speed } from './useScenarioPlayer';
import { ConversationList } from './components/ConversationList';
import { ChatThread } from './components/ChatThread';
import { QuestionChips } from './components/QuestionChips';
import { DemoInputBox } from './components/DemoInputBox';

interface BrainDemoPageProps {
  presentMode?: boolean;
}

export const BrainDemoPage = ({ presentMode = false }: BrainDemoPageProps): JSX.Element => {
  const player = useScenarioPlayer();

  return (
    <div
      className={cn(
        'flex flex-col',
        presentMode ? 'h-screen bg-white px-8 py-6' : 'h-[calc(100vh-7.5rem)]'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-4 border-b border-neutral-200 pb-3">
        <div className="flex items-center gap-2.5">
          <Brain className={cn('text-[#0000FF]', presentMode ? 'h-7 w-7' : 'h-6 w-6')} />
          <h1 className={cn('font-bold text-neutral-900', presentMode ? 'text-2xl' : 'text-xl')}>
            Company Brain
          </h1>
        </div>

        <div className="flex items-center gap-1.5">
          <ToggleGroup
            type="single"
            value={String(player.speed)}
            onValueChange={(v) => {
              if (!v || typeof v !== 'string') return;
              player.setSpeed((v === 'instant' ? 'instant' : Number(v)) as Speed);
            }}
            className="rounded-md border border-neutral-200 p-0.5"
          >
            <ToggleGroupItem value="1" aria-label="Нормална скорост" className="h-7 px-2 text-xs">
              1×
            </ToggleGroupItem>
            <ToggleGroupItem value="2" aria-label="Двойна скорост" className="h-7 px-2 text-xs">
              2×
            </ToggleGroupItem>
            <ToggleGroupItem value="instant" aria-label="Мигновено" className="h-7 px-2 text-xs">
              ⚡
            </ToggleGroupItem>
          </ToggleGroup>
          <Button
            variant="ghost"
            size="sm"
            disabled={!player.isPlaying}
            onClick={player.skip}
            title="Прескочи до края"
          >
            <SkipForward className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={player.clear} title="Нулирай демо разговорите">
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Two panes */}
      <div className="flex min-h-0 flex-1 gap-4 pt-3">
        <ConversationList
          convs={player.convs}
          activeKey={player.activeKey}
          onSelect={player.select}
          onNewChat={player.newChat}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          {player.activeConv ? (
            <ChatThread conv={player.activeConv} presentMode={presentMode} onSkip={player.skip} />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-5 overflow-y-auto py-6">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
                <Brain className="h-7 w-7 text-neutral-400" />
              </span>
              <div className="text-center">
                <p className="font-semibold text-neutral-900">Попитайте Company Brain</p>
                <p className="mt-1 max-w-md text-sm text-neutral-500">
                  Конвенции, решения и специфики на клиентите — или изберете от предложените
                  въпроси.
                </p>
              </div>
              <QuestionChips disabled={player.isPlaying} onPick={player.play} />
            </div>
          )}

          <div className="border-t border-neutral-200 pt-3">
            <DemoInputBox disabled={player.isPlaying} onPick={player.play} />
          </div>
        </div>
      </div>
    </div>
  );
};
